import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import { JSBI, Percent, Router, SwapParameters, Trade, TradeType } from '@uniswap/sdk'
import { useMemo } from 'react'
import { BIPS_BASE, DEFAULT_DEADLINE_FROM_NOW, INITIAL_ALLOWED_SLIPPAGE } from '../constants'
import { getTradeVersion, useV1TradeExchangeAddress } from '../data/V1'
import { useTransactionAdder } from '../state/transactions/hooks'
import { calculateGasMargin, getRouterContract, isAddress, shortenAddress } from '../utils'
import isZero from '../utils/isZero'
import v1SwapArguments from '../utils/v1SwapArguments'
import { useActiveWeb3React } from './index'
import { useV1ExchangeContract } from './useContract'
import useENS from './useENS'
import { Version } from './useToggledVersion'
import { getCurrencySymbol } from '../utils/getNativeTokenSymbol'

export enum SwapCallbackState {
  INVALID,
  LOADING,
  VALID
}

interface SwapCall {
  contract: Contract
  parameters: SwapParameters
}

interface SuccessfulCall {
  call: SwapCall
  gasEstimate: BigNumber
}

interface FailedCall {
  call: SwapCall
  error: Error
}

type EstimatedSwapCall = SuccessfulCall | FailedCall

const FEE_BUMP_BIPS = BigNumber.from(1200) // 12%
const BIPS_DENOMINATOR = BigNumber.from(10000)

function bumpFee(value: BigNumber | null | undefined): BigNumber | undefined {
  if (!value) return undefined
  return value.mul(BIPS_DENOMINATOR.add(FEE_BUMP_BIPS)).div(BIPS_DENOMINATOR)
}

function collectErrorMessages(error: any): string {
  const fragments = [
    error?.message,
    error?.reason,
    error?.data?.message,
    error?.data?.reason,
    error?.error?.message,
    error?.error?.reason,
    error?.error?.data?.message,
    error?.error?.data?.reason,
    error?.data?.originalError?.message,
    error?.data?.originalError?.reason,
    error?.error?.data?.originalError?.message,
    error?.error?.data?.originalError?.reason
  ].filter(Boolean)

  return fragments.join(' | ')
}

function isReplacementUnderpriced(error: any): boolean {
  const message = collectErrorMessages(error)
  return /replacement\s+(transaction|tx)\s+underpriced/i.test(message)
}

function isInternalJsonRpcError(error: any): boolean {
  const message = collectErrorMessages(error)
  return error?.code === -32603 || /internal json-rpc error/i.test(message)
}

function getDisplayErrorMessage(error: any): string {
  const message = collectErrorMessages(error)
  return message || `${error}`
}

function shouldRetryWithBumpedFee(error: any): boolean {
  return isReplacementUnderpriced(error) || isInternalJsonRpcError(error)
}

function getBumpedFeeOverrides(feeData: any): {
  gasPrice?: BigNumber
  maxFeePerGas?: BigNumber
  maxPriorityFeePerGas?: BigNumber
} {
  const maxFeePerGas = bumpFee(feeData?.maxFeePerGas)
  const maxPriorityFeePerGas = bumpFee(feeData?.maxPriorityFeePerGas)

  if (maxFeePerGas && maxPriorityFeePerGas) {
    return { maxFeePerGas, maxPriorityFeePerGas }
  }

  const gasPrice = bumpFee(feeData?.gasPrice)
  return gasPrice ? { gasPrice } : {}
}

/**
 * Returns the swap calls that can be used to make the trade
 * @param trade trade to execute
 * @param allowedSlippage user allowed slippage
 * @param deadline the deadline for the trade
 * @param recipientAddressOrName
 */
function useSwapCallArguments(
  trade: Trade | undefined, // trade to execute, required
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE, // in bips
  deadline: number = DEFAULT_DEADLINE_FROM_NOW, // in seconds from now
  recipientAddressOrName: string | null // the ENS name or address of the recipient of the trade, or null if swap should be returned to sender
): SwapCall[] {
  const { account, chainId, library } = useActiveWeb3React()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  const v1Exchange = useV1ExchangeContract(useV1TradeExchangeAddress(trade), true)

  return useMemo(() => {
    const tradeVersion = getTradeVersion(trade)
    if (!trade || !recipient || !library || !account || !tradeVersion || !chainId) return []

    const contract: Contract | null =
      tradeVersion === Version.v2 ? getRouterContract(chainId, library, account) : v1Exchange
    if (!contract) {
      return []
    }

    const swapMethods = []

    switch (tradeVersion) {
      case Version.v2:
        swapMethods.push(
          Router.swapCallParameters(trade, {
            feeOnTransfer: false,
            allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
            recipient,
            ttl: deadline
          })
        )

        if (trade.tradeType === TradeType.EXACT_INPUT) {
          swapMethods.push(
            Router.swapCallParameters(trade, {
              feeOnTransfer: true,
              allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
              recipient,
              ttl: deadline
            })
          )
        }
        break
      case Version.v1:
        swapMethods.push(
          v1SwapArguments(trade, {
            allowedSlippage: new Percent(JSBI.BigInt(allowedSlippage), BIPS_BASE),
            recipient,
            ttl: deadline
          })
        )
        break
    }
    return swapMethods.map(parameters => ({ parameters, contract }))
  }, [account, allowedSlippage, chainId, deadline, library, recipient, trade, v1Exchange])
}

// returns a function that will execute a swap, if the parameters are all valid
// and the user has approved the slippage adjusted input amount for the trade
export function useSwapCallback(
  trade: Trade | undefined, // trade to execute, required
  allowedSlippage: number = INITIAL_ALLOWED_SLIPPAGE, // in bips
  deadline: number = DEFAULT_DEADLINE_FROM_NOW, // in seconds from now
  recipientAddressOrName: string | null // the ENS name or address of the recipient of the trade, or null if swap should be returned to sender
): { state: SwapCallbackState; callback: null | (() => Promise<string>); error: string | null } {
  const { account, chainId, library } = useActiveWeb3React()

  const swapCalls = useSwapCallArguments(trade, allowedSlippage, deadline, recipientAddressOrName)

  const addTransaction = useTransactionAdder()

  const { address: recipientAddress } = useENS(recipientAddressOrName)
  const recipient = recipientAddressOrName === null ? account : recipientAddress

  return useMemo(() => {
    if (!trade || !library || !account || !chainId) {
      return { state: SwapCallbackState.INVALID, callback: null, error: 'Missing dependencies' }
    }
    if (!recipient) {
      if (recipientAddressOrName !== null) {
        return { state: SwapCallbackState.INVALID, callback: null, error: 'Invalid recipient' }
      } else {
        return { state: SwapCallbackState.LOADING, callback: null, error: null }
      }
    }

    const tradeVersion = getTradeVersion(trade)

    return {
      state: SwapCallbackState.VALID,
      callback: async function onSwap(): Promise<string> {
        const estimatedCalls: EstimatedSwapCall[] = await Promise.all(
          swapCalls.map(call => {
            const {
              parameters: { methodName, args, value },
              contract
            } = call
            const options = !value || isZero(value) ? {} : { value }

            return contract.estimateGas[methodName](...args, options)
              .then(gasEstimate => {
                return {
                  call,
                  gasEstimate
                }
              })
              .catch(gasError => {
                console.debug('Gas estimate failed, trying eth_call to extract error', call)

                return contract.callStatic[methodName](...args, options)
                  .then(result => {
                    console.debug('Unexpected successful call after failed estimate gas', call, gasError, result)
                    return { call, error: new Error('Unexpected issue with estimating the gas. Please try again.') }
                  })
                  .catch(callError => {
                    console.debug('Call threw error', call, callError)
                    let errorMessage: string
                    switch (callError.reason) {
                      case 'UniswapV2Router: INSUFFICIENT_OUTPUT_AMOUNT':
                      case 'UniswapV2Router: EXCESSIVE_INPUT_AMOUNT':
                        errorMessage =
                          'This transaction will not succeed either due to price movement or fee on transfer. Try increasing your slippage tolerance.'
                        break
                      default:
                        errorMessage = `The transaction cannot succeed due to error: ${callError.reason}. This is probably an issue with one of the tokens you are swapping.`
                    }
                    return { call, error: new Error(errorMessage) }
                  })
              })
          })
        )

        // a successful estimation is a bignumber gas estimate and the next call is also a bignumber gas estimate
        const successfulEstimation = estimatedCalls.find(
          (el, ix, list): el is SuccessfulCall =>
            'gasEstimate' in el && (ix === list.length - 1 || 'gasEstimate' in list[ix + 1])
        )

        if (!successfulEstimation) {
          const errorCalls = estimatedCalls.filter((call): call is FailedCall => 'error' in call)
          if (errorCalls.length > 0) throw errorCalls[errorCalls.length - 1].error
          throw new Error('Unexpected error. Please contact support: none of the calls threw an error')
        }

        const {
          call: {
            contract,
            parameters: { methodName, args, value }
          },
          gasEstimate
        } = successfulEstimation

        const baseTxOptions = {
          gasLimit: calculateGasMargin(gasEstimate),
          ...(value && !isZero(value) ? { value, from: account } : { from: account })
        }

        const sendSwap = (overrides: any = {}) => contract[methodName](...args, { ...baseTxOptions, ...overrides })

        let response: any
        try {
          response = await sendSwap()
        } catch (error: any) {
          // if the user rejected the tx, pass this along
          if (error?.code === 4001) {
            throw new Error('Transaction rejected.')
          }

          // Some wallets collapse node errors to -32603 "Internal JSON-RPC error".
          // Retry once with a fee bump to recover replacement/nonce related failures.
          if (shouldRetryWithBumpedFee(error)) {
            try {
              const bumpedFeeOverrides = getBumpedFeeOverrides(await library.getFeeData())
              if (Object.keys(bumpedFeeOverrides).length > 0) {
                response = await sendSwap(bumpedFeeOverrides)
              } else {
                throw error
              }
            } catch (retryError: any) {
              if (retryError?.code === 4001) {
                throw new Error('Transaction rejected.')
              }
              console.error(`Swap retry failed`, retryError, methodName, args, value)
              throw new Error(
                `Swap failed: ${getDisplayErrorMessage(
                  retryError
                )}. If you have a pending swap, speed it up or cancel it in your wallet and retry.`
              )
            }
          } else {
            // otherwise, the error was unexpected and we need to convey that
            console.error(`Swap failed`, error, methodName, args, value)
            throw new Error(`Swap failed: ${getDisplayErrorMessage(error)}`)
          }
        }

        const inputSymbol = getCurrencySymbol(trade.inputAmount.currency, chainId)
        const outputSymbol = getCurrencySymbol(trade.outputAmount.currency, chainId)
        const inputAmount = trade.inputAmount.toSignificant(3)
        const outputAmount = trade.outputAmount.toSignificant(3)

        const base = `Swap ${inputAmount} ${inputSymbol} for ${outputAmount} ${outputSymbol}`
        const withRecipient =
          recipient === account
            ? base
            : `${base} to ${
                recipientAddressOrName && isAddress(recipientAddressOrName)
                  ? shortenAddress(recipientAddressOrName)
                  : recipientAddressOrName
              }`

        const withVersion =
          tradeVersion === Version.v2 ? withRecipient : `${withRecipient} on ${(tradeVersion as any).toUpperCase()}`

        addTransaction(response, {
          summary: withVersion
        })

        return response.hash
      },
      error: null
    }
  }, [trade, library, account, chainId, recipient, recipientAddressOrName, swapCalls, addTransaction])
}
